import { MeasurementType } from '@/@types/global';
import { Injectable } from '@angular/core';

export type TagSeverity = 'success' | 'info' | 'warn' | 'danger';
export interface StatusInfo { type: TagSeverity, msg: string }

export type ActiveItem = MeasurementType;

export type Sex = 0 | 1; // 0=female, 1=male

export interface DataRow {
    '資料 ID': number;
    '量測日期': string;
    '來源': string;
    [k: string]: string | number;
}

function num(v: string | number | undefined): number | null {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

@Injectable({
    providedIn: 'root',
})
export class SeverityTagService {
    private _activeItem: ActiveItem = 'BloodPressure';
    private _sex: Sex = 1;
    // 快取
    private cache = new Map<string, StatusInfo | null>();

    setActiveItem(a: ActiveItem) {
        if (this._activeItem !== a) {
            this._activeItem = a;
            this.cache.clear();
        }
    }

    get activeItem() { return this._activeItem; }

    setSex(s: Sex) {
        if (this._sex !== s) {
            this._sex = s === 1 ? 1 : 0;
            this.cache.clear();
        }
    }

    get sex() { return this._sex; }

    /** 回傳對應的狀態；回傳 null 代表該欄位不適用 */
    getSeverity(columnName: string, data: DataRow): StatusInfo | null {
        const key = this.buildKey(columnName, data);
        if (this.cache.has(key)) return this.cache.get(key) ?? null;

        let res: StatusInfo | null;
        switch (this._activeItem) {
        case 'BloodPressure':
            res = this.evalBloodPressure(columnName, data);
            break;
        case 'BloodSugar':
            res = this.evalBloodSugar(columnName, data);
            break;
        case 'BloodOxygen':
            res = this.evalBloodOxygen(columnName, data);
            break;
        case 'BMI':
            res = this.evalBMI(columnName, data);
            break;
        case 'Waist':
            res = this.evalWaist(columnName, data);
            break;
        // HeartRate need to be deleted
        case 'HeartRate':
            res = this.evalHeartRate(columnName, data);
            break;
        case 'BodyTemperature':
            res = this.evalBodyTemp(columnName, data);
            break;
        default:
            res = null;
        }

        this.cache.set(key, res);
        return res;
    }

    // ---------- 個別指標邏輯 ----------

    /** 需要同時參考「收縮壓」與「舒張壓」兩欄 */
    private evalBloodPressure(column: string, data: DataRow): StatusInfo | null {
        if (column !== '收縮壓' && column !== '舒張壓') return null;

        const systolic = num(data['收縮壓']);
        const diastolic = num(data['舒張壓']);
        if (systolic == null || diastolic == null) return null;

        if (systolic > 180 || diastolic > 120) return { type: 'danger', msg: '高血壓危機' };
        if (systolic >= 140 || diastolic >= 90) return { type: 'warn', msg: '高血壓第二期' };
        if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89))
            return { type: 'warn', msg: '高血壓第一期' };
        if (systolic >= 120 && systolic <= 129 && diastolic < 80)
            return { type: 'info', msg: '血壓偏高' };
        if (systolic < 120 && diastolic < 80)
            return { type: 'success', msg: '正常' };
        return null;
    }

    /** 二擇一：空腹 or 飯後，優先空腹 */
    private evalBloodSugar(column: string, data: DataRow): StatusInfo | null {
        if (column !== 'bloodSugarValue') return null;
        const fasting = num(data['空腹血糖']);
        const postprandial = num(data['飯後血糖']);

        if (fasting != null) {
            if (fasting >= 126) return { type: 'danger', msg: '糖尿病' };
            if (fasting >= 100 && fasting <= 125) return { type: 'warn', msg: '糖尿病前期' };
            if (fasting >= 70 && fasting <= 99) return { type: 'success', msg: '正常' };
            return null;
        }
        else if (postprandial != null) {
            if (postprandial >= 200) return { type: 'danger', msg: '糖尿病' };
            if (postprandial >= 140 && postprandial <= 199) return { type: 'warn', msg: '糖尿病前期' };
            if (postprandial < 140) return { type: 'success', msg: '正常' };
            return null;
        }
        return null;
    }

    private evalBloodOxygen(column: string, data: DataRow): StatusInfo | null {
        if (column !== '血氧') return null;
        const v = num(data[column]);
        if (v == null) return null;
        if (v < 90) return { type: 'danger', msg: '嚴重缺氧' };
        if (v >= 90 && v <= 94) return { type: 'warn', msg: '輕微缺氧' };
        if (v >= 95 && v <= 100) return { type: 'success', msg: '正常' };
        return null;
    }

    private evalBMI(column: string, data: DataRow): StatusInfo | null {
        if (column !== '身體質量指數') return null;
        const v = num(data[column]);
        if (v == null) return null;
        if (v >= 27) return { type: 'danger', msg: '肥胖' };
        if (v >= 24 && v < 27) return { type: 'warn', msg: '過重' };
        if (v < 18.5) return { type: 'warn', msg: '過輕' };
        if (v >= 18.5 && v < 24) return { type: 'success', msg: '正常' };
        return null;
    }

    private evalWaist(column: string, data: DataRow): StatusInfo | null {
        if (column !== '腰圍') return null;
        const v = num(data[column]);
        if (v == null) return null;

        if (this._sex === 0) { // male
            if (v > 100) return { type: 'danger', msg: '高度超標' };
            if (v >= 90 && v <= 99.9) return { type: 'warn', msg: '輕度超標' };
            if (v < 90) return { type: 'success', msg: '正常' };
        }
        else { // female
            if (v > 88) return { type: 'danger', msg: '高度超標' };
            if (v >= 80 && v <= 87.9) return { type: 'warn', msg: '輕度超標' };
            if (v < 80) return { type: 'success', msg: '正常' };
        }
        return null;
    }

    private evalHeartRate(column: string, data: DataRow): StatusInfo | null {
        if (column !== '心律') return null;
        const v = num(data[column]);
        if (v == null) return null;
        if (v > 100) return { type: 'danger', msg: '過速' };
        if (v >= 80 && v <= 100) return { type: 'warn', msg: '偏高' };
        if (v < 60) return { type: 'warn', msg: '偏低' };
        if (v >= 60 && v < 80) return { type: 'success', msg: '正常' };
        return null;
    }

    private evalBodyTemp(column: string, data: DataRow): StatusInfo | null {
        if (column !== '體溫') return null;
        const v = num(data[column]);
        if (v == null) return null;
        if (v > 40) return { type: 'danger', msg: '高燒' };
        if (v >= 38 && v <= 40) return { type: 'warn', msg: '低燒' };
        if (v < 35) return { type: 'warn', msg: '低體溫' };
        if (v >= 35 && v < 38) return { type: 'success', msg: '正常' };
        return null;
    }

    private buildKey(column: string, data: DataRow): string {
        return `${data['資料 ID'].toString()}-${column}`;
    }
}
